import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Mic } from "lucide-react";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import NexoCore from "./NexoCore";

// Sessão UI-1 — HeroPanel premium.
//
// Substitui o HeroSection antigo (orbe centralizado + greeting time).
// Layout 2 colunas em desktop, stack em mobile (orbe acima da saudação).
// Subtítulo dinâmico derivado do briefing com 4 fallbacks.
//
// Não toca em NexoCore (canvas blindado) — só passa state/size/onClick.
// Hover preview do orbe ("listening") só ativa quando state real é "idle"
// pra não competir com o pipeline de voz real.

// Resolve UI state pro voice status text (mesmo padrão do HeroSection antigo).
function resolveUiState(state, vadSpeaking) {
  if (state === "connecting") return "connecting";
  if (state === "listening" && vadSpeaking) return "user-speaking";
  if (state === "listening") return "armed";
  if (state === "processing") return "processing";
  if (state === "speaking") return "responding";
  return "idle";
}

const UI_STATE_CONFIG = {
  idle:            { label: null,                  color: null,                pulse: false },
  connecting:      { label: "Conectando…",         color: "text-brand-cyan",   pulse: true  },
  armed:           { label: "Pode falar…",         color: "text-brand-cyan",   pulse: true  },
  "user-speaking": { label: "Te escutando",        color: "text-brand-green",  pulse: false },
  processing:      { label: "Processando…",        color: "text-brand-purple", pulse: false },
  responding:      { label: "Nexo falando…",       color: "text-brand-cyan",   pulse: false },
};

function getFirstName(user) {
  if (!user) return null;
  const raw = (user.name || user.email || "").trim();
  if (!raw) return null;
  const first = raw.split(/\s+/)[0];
  // Se for email, pega o local-part antes do @
  if (first.includes("@")) return first.split("@")[0];
  return first;
}

// Subtítulo dinâmico — 4 fallbacks na ordem do brief.
function deriveSubtitle(briefing) {
  if (!briefing) return "Tudo tranquilo por aqui. O que vamos fazer hoje?";

  if (briefing.context_message && typeof briefing.context_message === "string") {
    return briefing.context_message;
  }

  const actions = briefing.priority_actions || [];
  if (actions.length > 0) {
    const first = actions[0];
    const verb = first?.label || "ação prioritária";
    const n = actions.length;
    return n === 1
      ? `Você tem 1 ação prioritária: ${verb}. Quer revisar?`
      : `Você tem ${n} ações prioritárias. Quer revisar?`;
  }

  const deadlines = briefing.upcoming_deadlines || [];
  if (deadlines.length > 0) {
    const d = deadlines[0];
    const title = d?.title || "compromisso";
    let when = "em breve";
    if (d?.datetime) {
      try {
        when = formatDistanceToNow(parseISO(d.datetime), { addSuffix: true, locale: ptBR });
      } catch (_) { /* keep default */ }
    }
    return `Próximo: ${title} ${when}.`;
  }

  return "Tudo tranquilo por aqui. O que vamos fazer hoje?";
}

export default function HeroPanel({
  state = "idle",
  onMicClick,
  briefing = null,
  transcript = "",
  isVoiceActive = false,
  vadSpeaking = false,
}) {
  const { user } = useAuth();
  const firstName = getFirstName(user);
  const [hovered, setHovered] = useState(false);

  // Hover preview "listening" só quando idle de fato — protege voice pipeline.
  const renderState = (state === "idle" && hovered && !isVoiceActive) ? "listening" : state;

  const uiState = resolveUiState(state, vadSpeaking);
  const uiCfg = UI_STATE_CONFIG[uiState];

  const orbSubtext = useMemo(() => {
    if ((uiState === "armed" || uiState === "user-speaking") && transcript) return `"${transcript}"`;
    return uiCfg.label;
  }, [uiState, uiCfg, transcript]);

  const dateLabel = useMemo(() => {
    try {
      return format(new Date(), "EEEE, dd MMM", { locale: ptBR }).toUpperCase();
    } catch (_) { return ""; }
  }, []);

  const subtitle = useMemo(() => deriveSubtitle(briefing), [briefing]);

  const greetingTitle = firstName ? `Olá, ${firstName}!` : "Olá!";

  return (
    <section
      className="relative px-6 md:px-12 py-10 md:py-14 overflow-hidden rounded-2xl glass-card-strong gradient-border-cyan glow-hover"
      data-testid="hero-panel"
    >
      {/* Gradient radial sutil emanando da posição do orbe (esquerda) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 22% 50%, rgba(0,212,255,0.18), transparent 55%)",
        }}
      />

      <div className="relative grid grid-cols-1 md:grid-cols-[minmax(220px,42%)_1fr] gap-8 md:gap-12 items-center">
        {/* Coluna esquerda — NexoCore */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col items-center md:items-end justify-center"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div className="relative">
            {/* UI-1.5 — Glow radial ambient atrás do canvas. NexoCore.jsx
                permanece intocado (decisão dura: canvas é blindado). Aqui
                só o wrapper externo recebe um halo cyan suave que pulsa
                no compasso CSS, dando vida ao orbe sem mexer no painter
                interno. animate-pulse só quando idle pra não competir
                com os estados ativos do orbe. */}
            <div
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute inset-[-40px] z-0",
                state === "idle" && !isVoiceActive && "motion-safe:animate-pulse",
              )}
              style={{
                background:
                  "radial-gradient(circle, rgba(0,212,255,0.25), transparent 70%)",
                filter: "blur(20px)",
              }}
            />
            {/* mobile: 200, md: 260, lg: 300 — sempre par + cabe em viewport mobile */}
            <div className="md:hidden relative z-10">
              <NexoCore state={renderState} size={200} onClick={onMicClick} />
            </div>
            <div className="hidden md:block lg:hidden relative z-10">
              <NexoCore state={renderState} size={260} onClick={onMicClick} />
            </div>
            <div className="hidden lg:block relative z-10">
              <NexoCore state={renderState} size={300} onClick={onMicClick} />
            </div>

            <AnimatePresence mode="wait">
              {orbSubtext ? (
                <motion.div
                  key={orbSubtext}
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.2 }}
                  className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap"
                >
                  <span
                    className={cn(
                      "text-caption font-light uppercase inline-flex items-center gap-2",
                      uiCfg.color,
                      uiCfg.pulse && "motion-safe:animate-pulse",
                    )}
                    style={{ letterSpacing: "0.12em" }}
                    data-testid="voice-status-text"
                    data-ui-state={uiState}
                  >
                    {orbSubtext}
                  </span>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Coluna direita — saudação */}
        <div className="flex flex-col items-center md:items-start text-center md:text-left max-w-2xl">
          <motion.span
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.0 }}
            className="text-caption text-brand-cyan/80 mb-3 font-semibold"
            style={{ letterSpacing: "0.16em" }}
            data-testid="hero-eyebrow"
          >
            {dateLabel}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className="font-display font-bold text-foreground text-[40px] md:text-[56px] lg:text-[64px] leading-[1.05] tracking-[-0.02em] mb-3"
            data-testid="hero-greeting"
          >
            {greetingTitle}
          </motion.h1>

          {/* Mobile Parity Etapa 3: o subtitle vem de briefing.context_message
              (AI) e pode chegar com parágrafos longos que quebram o hero em
              mobile. line-clamp-3 limita a 3 linhas com ellipsis em <md;
              desktop preservado (md:line-clamp-none). text-sm em mobile
              reduz proporção vs o h1 já grande (40px em mobile). */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.16 }}
            className="text-sm md:text-body text-foreground-muted mb-7 max-w-xl line-clamp-3 md:line-clamp-none"
            data-testid="hero-subtitle"
            title={typeof subtitle === "string" ? subtitle : undefined}
          >
            {subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.24 }}
            className="w-full md:w-auto"
          >
            <Button
              variant="primary-tech"
              size="lg"
              onClick={onMicClick}
              className={cn(
                "w-full md:w-auto",
                // Pulse sutil só quando voice está ocioso. Durante listening/
                // processing/speaking o pulse pararia competindo com o estado real.
                state === "idle" && !isVoiceActive && "pulse-glow",
              )}
              data-testid="hero-cta-talk"
              aria-label="Falar com Nexo"
            >
              <Sparkles className="w-4 h-4" />
              Falar com Nexo
              {!isVoiceActive && <Mic className="w-4 h-4 opacity-70" />}
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
